import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  applyBorkRun,
  BorkRunExpiredError,
  BorkRunFailedError,
  BorkRunStillRunningError,
  BorkStreamError,
  deleteBorkConnection,
  getAdminRestaurantsList,
  getBorkRunStatus,
  listBorkConnections,
  runBorkFetchStream,
  saveBorkConnection,
  type AdminRestaurant,
  type BorkConnection,
  type BorkDayResult,
  type BorkFetchSummary,
} from '../../../../../../services/admin/adminService';
import { ChevronDown, ChevronLeft, ChevronRight, Pencil, Plug, Plus, Trash2 } from 'lucide-react';
import Toggle from '../../../../../ui/Toggle';
import { fmtLocalDate } from '../ranges';
import { BorkConnectionSlideOver } from './BorkConnectionSlideOver';
import { applicableDayKeys, borkDayKey, BorkFetchResults, issueDayKeys } from './BorkFetchResults';
import { BorkLastRunChip } from './borkShared';
import type { TFn } from '../../types';

/** YYYY-MM-DD `n` days back — same idiom as the other AI-checker panels. */
const isoDaysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/** Newest business day the POS can have closed — today's revenue is still running. */
const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

/** First day of the month `iso` falls in; null while the date box is empty or half-typed. */
const anchorOf = (iso: string): Date | null => {
  const [y, m] = iso.split('-').map(Number);
  return Number.isFinite(y) && Number.isFinite(m) ? new Date(y, m - 1, 1) : null;
};

/** The `chunkMonths` whole calendar months from `anchor`, with the end clamped to yesterday. */
function monthWindow(anchor: Date, chunkMonths: number): { startDate: string; endDate: string } {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + chunkMonths, 0);
  const cap = yesterday();
  return { startDate: fmtLocalDate(start), endDate: fmtLocalDate(end > cap ? cap : end) };
}

/** How often recovery asks the server where a dropped run has got to. */
const POLL_INTERVAL_MS = 10_000;
/** Consecutive poll failures before recovery gives up rather than hammering a dead server. */
const POLL_MAX_FAILURES = 5;
/**
 * Polls with no forward progress before recovery calls the run dead. A run that died mid-fetch keeps
 * answering `running` forever, so without this the panel would watch a corpse indefinitely. Sized
 * above the worst LEGITIMATE single-day fetch (~6.2 min: 120s timeout + 2s backoff + 120s + 6s
 * backoff + 120s), because at the tail of a run one slow venue can be the only counter-mover — a
 * tighter bound would cry wolf exactly when the operator is 40 minutes deep.
 */
const POLL_STALL_LIMIT = 45; // 7.5 min at POLL_INTERVAL_MS

/**
 * Bar + label state. The live stream fills in which restaurant-day it is on; recovery polling only
 * knows the counts, so the detail fields are optional and the label degrades to "n / total days".
 */
type RunProgress = { completedDays: number; totalDays: number; restaurantName?: string; date?: string };

/** Identity of one source row across a dry run and the apply response that rewrote it. */
const rowKey = (r: BorkDayResult) => `${r.restaurantId}|${r.date}|${r.sourceType ?? 'venue'}`;

/**
 * Fold an apply response back into the dry run on screen: an applied row replaces its dry-run twin
 * (that copy is the one carrying the inserted/replaced badge) and the write counters accumulate, so
 * applying twice from one review reads correctly.
 *
 * The two row sets do NOT zip 1:1 — apply re-reads stored state, so it can return a row the dry run
 * never had (a `revenue_history` row that appeared in between), return nothing for a dry row (one
 * that disappeared), or collapse a whole day to a single venue-level row. Every case is merged by
 * key rather than by position.
 */
function mergeApplied(prev: BorkFetchSummary, applied: BorkFetchSummary): BorkFetchSummary {
  // A venue-level row (no source type) means apply gave up on the entire day — it supersedes every
  // per-type row the dry run showed for that restaurant-day instead of sitting next to them.
  const supersededDays = new Set(applied.results.filter((r) => r.sourceType === null).map(borkDayKey));
  const byKey = new Map(applied.results.map((r) => [rowKey(r), r]));
  const kept = prev.results.filter((r) => !supersededDays.has(borkDayKey(r)));
  const known = new Set(kept.map(rowKey));
  return {
    // Spreading `prev` keeps the dry run's own date range: apply reports the min/max of the days it
    // touched, which is narrower and would misdescribe the table.
    ...prev,
    inserted: prev.inserted + applied.inserted,
    replaced: prev.replaced + applied.replaced,
    removed: prev.removed + applied.removed,
    results: [
      ...kept.map((r) => byKey.get(rowKey(r)) ?? r),
      // Rows apply produced that no dry row matches — appended, never dropped.
      ...applied.results.filter((r) => !known.has(rowKey(r))),
    ],
  };
}

/**
 * Bork direct API connector — a collapsible panel inside the AI Checker view.
 *
 * Two halves. **Connections** stores one trivecgateway base URL + appid per restaurant (the appid is
 * write-only; the list only ever shows its last 4 characters). **Get Bork revenue** walks the history
 * in month-sized chunks: the dry run streams the range day by day from the POS gateway, diffs it
 * against stored `revenue_history` and writes NOTHING — the operator then ticks the restaurant-days
 * worth keeping and applies them (missing days only) or overrides them (stored rows deleted and
 * replaced). Writing always goes through a reviewed dry run, never straight from a date range.
 */
export function BorkDirectApiPanel({ t }: { t: TFn }) {
  const [open, setOpen] = useState(false);

  const [connections, setConnections] = useState<BorkConnection[]>([]);
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [connLoading, setConnLoading] = useState(false);
  const [connError, setConnError] = useState<string | null>(null);
  const [editing, setEditing] = useState<BorkConnection | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(() => isoDaysAgo(7));
  const [endDate, setEndDate] = useState(() => isoDaysAgo(1));
  const [chunkMonths, setChunkMonths] = useState<1 | 3>(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'streaming' | 'recovering' | 'done' | 'error'>('idle');
  const [progress, setProgress] = useState<RunProgress | null>(null);
  /** Survives a dropped stream so the run can be picked back up — same session only, no storage. */
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [summary, setSummary] = useState<BorkFetchSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState<'apply' | 'override' | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  /** The server dropped the cached dry run — nothing can be applied until it is re-run. */
  const [runExpired, setRunExpired] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /** Cancellation ticket for the recovery poll loop — flipped to stop it. */
  const pollRef = useRef<{ cancelled: boolean } | null>(null);
  /** Latest progress, readable from inside the poll loop, whose closure would otherwise see a
   *  stale render's copy. Mirrored by an effect so every setProgress call site feeds it. */
  const progressRef = useRef<RunProgress | null>(null);

  // Scope resets to "all" on every reload: the list only reloads on open / add / delete, so a
  // narrowed selection can never silently outlive the connection set it was picked from.
  const reloadConnections = useCallback(async () => {
    try {
      setConnLoading(true);
      setConnError(null);
      const list = await listBorkConnections();
      setConnections(list);
      setSelectedIds(list.map((c) => c.restaurantId));
    } catch (e: any) {
      setConnError(e.message || 'Failed to load Bork connections');
    } finally {
      setConnLoading(false);
    }
  }, []);

  // Everything loads on first expand — the panel is collapsed by default and most AI Checker
  // sessions never touch it.
  useEffect(() => {
    if (!open) return;
    reloadConnections();
    getAdminRestaurantsList().then(setRestaurants).catch(() => setRestaurants([]));
  }, [open, reloadConnections]);

  useEffect(() => { progressRef.current = progress; }, [progress]);

  // A dry run can stream for the better part of an hour — drop the socket and stop polling if the
  // operator navigates away. The run itself keeps going server-side either way.
  useEffect(() => () => {
    abortRef.current?.abort();
    if (pollRef.current) pollRef.current.cancelled = true;
  }, []);

  const toggleScope = (restaurantId: string) =>
    setSelectedIds((ids) => (ids.includes(restaurantId) ? ids.filter((x) => x !== restaurantId) : [...ids, restaurantId]));

  // Updated in place rather than via reloadConnections(), which would reset the fetch scope below —
  // flipping one venue's cron flag must not silently widen which restaurants the next pull covers.
  const toggleEnabled = async (c: BorkConnection, next: boolean) => {
    if (!c.baseUrl) return;
    const previous = connections;
    setConnections((cs) => cs.map((x) => (x.clientId === c.clientId ? { ...x, isEnabled: next } : x)));
    try {
      setTogglingId(c.clientId);
      setConnError(null);
      const updated = await saveBorkConnection(c.restaurantId, { baseUrl: c.baseUrl, isEnabled: next });
      setConnections((cs) => cs.map((x) => (x.clientId === c.clientId ? updated : x)));
    } catch (e: any) {
      setConnections(previous);
      setConnError(e.message || 'Failed to update connection');
    } finally {
      setTogglingId(null);
    }
  };

  const removeConnection = async (c: BorkConnection) => {
    if (!window.confirm(t('adminTesting.bork.deleteConfirm', { restaurant: c.restaurantName ?? c.name }))) return;
    try {
      setDeletingId(c.restaurantId);
      setConnError(null);
      await deleteBorkConnection(c.restaurantId);
      await reloadConnections();
    } catch (e: any) {
      setConnError(e.message || 'Failed to delete connection');
    } finally {
      setDeletingId(null);
    }
  };

  /** Jump the range one chunk back/forward, snapped to whole calendar months. */
  const stepWindow = (direction: -1 | 1) => {
    const anchor = anchorOf(startDate) ?? yesterday();
    const next = new Date(anchor.getFullYear(), anchor.getMonth() + direction * chunkMonths, 1);
    const w = monthWindow(next, chunkMonths);
    setStartDate(w.startDate);
    setEndDate(w.endDate);
  };

  /** Re-cut the range at the new size, keeping the month it currently starts in. */
  const changeChunk = (months: 1 | 3) => {
    setChunkMonths(months);
    const anchor = anchorOf(startDate);
    if (!anchor) return;
    const w = monthWindow(anchor, months);
    setStartDate(w.startDate);
    setEndDate(w.endDate);
  };

  /**
   * Follow a run the stream is no longer reporting on. The dry run outlives its socket, so a drop
   * mid-way through a 40-minute pull is a reconnect, not a loss — poll until the server hands over
   * the same summary the stream would have.
   */
  const recoverRun = async (runId: string) => {
    if (pollRef.current) pollRef.current.cancelled = true;
    const ticket = { cancelled: false };
    pollRef.current = ticket;
    setStatus('recovering');
    setError(null);
    let failures = 0;
    let stalledPolls = 0;
    let lastCompleted = -1;

    while (!ticket.cancelled) {
      try {
        const state = await getBorkRunStatus(runId);
        failures = 0;
        if (ticket.cancelled) return;
        if (state.status === 'complete') {
          setSummary(state.summary);
          setSelectedDays(new Set(issueDayKeys(state.summary)));
          setProgress(null);
          setStatus('done');
          return;
        }
        if (state.status === 'failed') {
          // The server says the run died. Terminal, and there is nothing left to recover, so the
          // recover affordance retires with it exactly as an expired run's does. How far it got
          // comes from the last count we saw rather than the response (which carries none) — a
          // little stale if the run advanced after the socket died, but its only job is telling
          // the operator whether to re-run the whole range or split it.
          const seen = progressRef.current;
          setRunExpired(true);
          setError(seen
            ? `${state.error}\n\n${t('adminTesting.bork.runFailedAfter', { done: seen.completedDays, total: seen.totalDays })}`
            : state.error);
          setStatus('error');
          return;
        }
        setProgress({ completedDays: state.completedDays, totalDays: state.totalDays });
        // `runExpired` stays false on a stall: the run may just be slow, so the recover button
        // survives and the operator can pick the watch back up.
        if (state.completedDays === lastCompleted) {
          if (++stalledPolls >= POLL_STALL_LIMIT) {
            setError(t('adminTesting.bork.runStalled'));
            setStatus('error');
            return;
          }
        } else {
          stalledPolls = 0;
          lastCompleted = state.completedDays;
        }
      } catch (e: any) {
        if (ticket.cancelled) return;
        if (e instanceof BorkRunExpiredError) {
          // The run is gone for good, so retiring the recover affordance with it.
          setRunExpired(true);
          setError(t('adminTesting.bork.runExpired'));
          setStatus('error');
          return;
        }
        // A blip while recovering from a blip is expected — retry, but don't poll a dead server
        // forever. The runId stays put, so the operator can still trigger recovery by hand.
        if (++failures >= POLL_MAX_FAILURES) {
          console.error('[Bork] run recovery polling failed:', e);
          setError(e.message || 'Failed to recover Bork run');
          setStatus('error');
          return;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  };

  const runDryRun = async () => {
    if (selectedIds.length === 0) return;
    abortRef.current?.abort();
    if (pollRef.current) pollRef.current.cancelled = true;
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('streaming');
    setError(null);
    setApplyError(null);
    setRunExpired(false);
    setProgress(null);
    setSummary(null);
    setLastRunId(null);
    // A held-over selection belongs to the previous run's runId, so it goes with the results.
    setSelectedDays(new Set());
    // Read back through a holder: state set inside the handlers is not readable from this closure,
    // and a stream that ends without a summary frame must not read as "done".
    const received: { runId: string | null; summary: BorkFetchSummary | null } = { runId: null, summary: null };
    try {
      await runBorkFetchStream(
        { startDate, endDate, restaurantIds: selectedIds },
        {
          onStarted: (s) => {
            // The run exists server-side from here on, so hold its id before anything can drop.
            received.runId = s.runId;
            setLastRunId(s.runId);
            setProgress({ completedDays: 0, totalDays: s.totalDays });
          },
          onProgress: setProgress,
          onSummary: (s) => {
            received.summary = s;
            setSummary(s);
            // Only the days that actually need action — an all-green day the operator would have to
            // untick by hand before overriding is not pre-ticked.
            setSelectedDays(new Set(issueDayKeys(s)));
          },
        },
        controller.signal,
      );
      if (!received.summary) {
        // The socket closed early. If the run got as far as announcing itself it is still going on
        // the server, so pick it up by polling rather than reporting a dead end.
        if (received.runId) return recoverRun(received.runId);
        setError(t('adminTesting.bork.streamIncomplete'));
        setStatus('error');
        return;
      }
      setStatus('done');
    } catch (e: any) {
      if (controller.signal.aborted) return; // superseded by a newer run, or the panel unmounted
      console.error('[Bork] dry run stream failed:', e);
      // An explicit error frame is the server saying WHY it stopped, and it stopped for good — the
      // day-cap message in particular tells the operator how to split the range. Polling it would
      // bury that message and watch a run that is never going to finish. Only a dropped socket,
      // which leaves the run alive, is worth recovering.
      if (e instanceof BorkStreamError) {
        setError(e.message);
        setStatus('error');
        return;
      }
      if (received.runId) return recoverRun(received.runId);
      setError(e.message || 'Failed to run Bork fetch');
      setStatus('error');
    }
  };

  const applySelected = async (mode: 'apply' | 'override') => {
    if (!summary?.runId || selectedDays.size === 0 || applying) return;
    if (!window.confirm(t(mode === 'override' ? 'adminTesting.bork.overrideConfirm' : 'adminTesting.bork.applyConfirm'))) return;
    try {
      setApplying(mode);
      setApplyError(null);
      const days = Array.from(selectedDays).map((key) => {
        const [restaurantId, date] = key.split('|');
        return { restaurantId, date };
      });
      const applied = await applyBorkRun({ runId: summary.runId, mode, days });
      const merged = mergeApplied(summary, applied);
      setSummary(merged);
      // Re-derive against the same issue rule: everything this apply just wrote now carries an
      // `applied` flag and drops out, so what stays ticked is exactly what Override still has to
      // fix. Filtering rather than replacing keeps the set from growing back behind the operator —
      // a day they deliberately unticked stays unticked. It also clears days apply collapsed to a
      // venue-level error, whose checkbox is now disabled and could not be unticked by hand.
      const stillIssues = new Set(issueDayKeys(merged));
      setSelectedDays((picked) => new Set(Array.from(picked).filter((key) => stillIssues.has(key))));
    } catch (e: any) {
      if (e instanceof BorkRunExpiredError) {
        setRunExpired(true);
        setApplyError(t('adminTesting.bork.runExpired'));
      } else if (e instanceof BorkRunFailedError) {
        // Terminal like an expired run — lock the buttons — but name the actual cause.
        setRunExpired(true);
        setApplyError(t('adminTesting.bork.applyRunFailed'));
      } else if (e instanceof BorkRunStillRunningError) {
        // Not "come back later": the in-flight guard already rejects apply while a stream is live,
        // so reaching this means the run died mid-fetch and can never complete. Treated like an
        // expired run — the buttons lock and the only way forward is a fresh dry run.
        setRunExpired(true);
        setApplyError(t('adminTesting.bork.applyStillRunning'));
      } else {
        console.error('[Bork] apply failed:', e);
        setApplyError(e.message || 'Failed to apply Bork run');
      }
    } finally {
      setApplying(null);
    }
  };

  const toggleDay = (dayKey: string) =>
    setSelectedDays((days) => {
      const next = new Set(days);
      if (next.has(dayKey)) next.delete(dayKey);
      else next.add(dayKey);
      return next;
    });

  const inputCls = cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);
  const iconBtnCls = cn('p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed', primaryText);
  const chunkBtnCls = (active: boolean) =>
    cn('px-2.5 py-1.5', smallTextSize, semiBoldText,
      active
        ? 'bg-sky-600 dark:bg-sky-600 text-white dark:text-white'
        : cn('bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText));
  // A recovering run is every bit as live as a streaming one — the work is still happening, we are
  // just watching it over polling instead of the socket.
  const running = status === 'streaming' || status === 'recovering';
  // Not while an apply is in flight: a new run clears the results its response would merge into.
  const canRun = !running && !applying && selectedIds.length > 0;
  // The next chunk would start after the last closed business day — there is nothing there to pull.
  const nextChunkInFuture = (() => {
    const anchor = anchorOf(startDate);
    return !anchor || new Date(anchor.getFullYear(), anchor.getMonth() + chunkMonths, 1) > yesterday();
  })();
  const canApply = !!summary?.runId && !runExpired && selectedDays.size > 0 && !applying && !running;
  const progressPct = progress && progress.totalDays > 0 ? Math.round((progress.completedDays / progress.totalDays) * 100) : 0;

  return (
    <div className={cn(cardClass, 'px-3 py-4 sm:px-4')}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Plug className="w-4 h-4 shrink-0 text-sky-600 dark:text-sky-400" />
          <h3 className={cn(primaryText, semiBoldText, 'truncate')}>{t('adminTesting.bork.title')}</h3>
        </div>
        {open ? <ChevronDown className={cn('w-4 h-4 shrink-0', secondaryText)} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', secondaryText)} />}
      </button>

      {open && (
        <div className="mt-4 space-y-6">
          <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.desc')}</p>

          {/* 1 — Connections (settings) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>{t('adminTesting.bork.connectionsTitle')}</h4>
              <button
                onClick={() => { setEditing(null); setFormOpen(true); }}
                className={cn('inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 dark:bg-sky-600 text-white dark:text-white hover:bg-sky-700 dark:hover:bg-sky-700', smallTextSize, semiBoldText)}
              >
                <Plus className="w-4 h-4" />
                {t('adminTesting.bork.addConnection')}
              </button>
            </div>

            {connError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{connError}</div>
            )}

            {connLoading ? (
              <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.connectionsLoading')}</div>
            ) : connections.length === 0 ? (
              <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.bork.noConnections')}</div>
            ) : (
              <div className="space-y-1.5">
                {connections.map((c) => (
                  <div key={c.clientId} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className={cn(primaryText, semiBoldText, smallTextSize, 'truncate')}>
                        {/* A null restaurantName means the config points at a restaurant that no longer
                            exists — show the client name so the row can still be found and deleted. */}
                        {c.restaurantName ?? c.name}
                        {c.centerName && <span className={cn(secondaryText, 'font-normal')}> · {c.centerName}</span>}
                      </div>
                      <div className={cn(secondaryText, 'text-xs font-mono truncate')} title={c.baseUrl ?? ''}>
                        {c.baseUrl ?? '—'} · {t('adminTesting.bork.appIdMasked', { masked: c.appIdMasked ?? '—' })}
                      </div>
                    </div>
                    <BorkLastRunChip connection={c} t={t} />
                    {/* A row with no base URL cannot be saved (the PUT requires one), so it cannot be
                        enabled either — the toggle would 400 rather than do nothing. */}
                    <span className="flex shrink-0" title={c.baseUrl ? undefined : t('adminTesting.bork.enableNeedsBaseUrl')}>
                      <Toggle
                        checked={c.isEnabled}
                        onChange={(next) => toggleEnabled(c, next)}
                        size="sm"
                        disabled={togglingId === c.clientId || !c.baseUrl}
                        ariaLabel={t('adminTesting.bork.enabledToggleAria', { restaurant: c.restaurantName ?? c.name })}
                      />
                    </span>
                    <button onClick={() => { setEditing(c); setFormOpen(true); }} title={t('adminTesting.bork.edit')} className={iconBtnCls}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeConnection(c)}
                      disabled={deletingId === c.restaurantId}
                      title={t('adminTesting.bork.delete')}
                      className={cn(iconBtnCls, 'text-red-600 dark:text-red-400')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 2 — Get Bork revenue: chunked dry run → review → selective apply */}
          <div className="space-y-3">
            <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>{t('adminTesting.bork.fetchTitle')}</h4>
            <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.fetchDesc')}</p>

            <div className="flex flex-wrap items-center gap-2">
              <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.chunkLabel')}</span>
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button onClick={() => changeChunk(1)} className={chunkBtnCls(chunkMonths === 1)}>{t('adminTesting.bork.chunk1Month')}</button>
                <button onClick={() => changeChunk(3)} className={cn(chunkBtnCls(chunkMonths === 3), 'border-l border-gray-300 dark:border-gray-600')}>{t('adminTesting.bork.chunk3Months')}</button>
              </div>
              <button onClick={() => stepWindow(-1)} title={t('adminTesting.bork.prevChunk')} aria-label={t('adminTesting.bork.prevChunk')} className={iconBtnCls}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => stepWindow(1)}
                disabled={nextChunkInFuture}
                title={t('adminTesting.bork.nextChunk')}
                aria-label={t('adminTesting.bork.nextChunk')}
                className={iconBtnCls}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.bork.startDateLabel')}</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={cn(inputCls, 'w-full')} />
              </div>
              <div className="w-40">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.bork.endDateLabel')}</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={cn(inputCls, 'w-full')} />
              </div>
              <button
                onClick={runDryRun}
                disabled={!canRun}
                className={cn('px-4 py-2 rounded-lg bg-sky-600 dark:bg-sky-600 text-white dark:text-white hover:bg-sky-700 dark:hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
              >
                {running ? t('adminTesting.bork.running') : t('adminTesting.bork.dryRun')}
              </button>
            </div>

            {running && (
              <div className="space-y-1">
                {status === 'recovering' && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm">
                    {t('adminTesting.bork.recovering')}
                  </div>
                )}
                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div className="h-full bg-sky-600 dark:bg-sky-500 transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                <div className={cn(secondaryText, 'text-xs')}>
                  {/* Polling knows the counts but not which day is in flight, so the label drops the
                      restaurant/date half rather than printing blanks. */}
                  {!progress
                    ? t('adminTesting.bork.progressStarting')
                    : progress.restaurantName && progress.date
                    ? t('adminTesting.bork.progressLine', {
                        done: progress.completedDays,
                        total: progress.totalDays,
                        restaurant: progress.restaurantName,
                        date: progress.date,
                      })
                    : t('adminTesting.bork.progressDaysOnly', { done: progress.completedDays, total: progress.totalDays })}
                </div>
              </div>
            )}

            {connections.length > 0 && (
              <div className="space-y-1">
                <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.scopeLabel')}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {connections.map((c) => (
                    <label key={c.clientId} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.restaurantId)}
                        onChange={() => toggleScope(c.restaurantId)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-sky-600 dark:text-sky-600 focus:ring-sky-500 dark:focus:ring-sky-500"
                      />
                      <span className={cn(secondaryText, 'text-xs')}>{c.restaurantName ?? c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm whitespace-pre-wrap">{error}</div>
                {/* The run may well have finished server-side while the UI sat in an error state —
                    offer to go and look before making the operator pay for a 40-minute re-run. */}
                {status === 'error' && lastRunId && !runExpired && (
                  <button
                    onClick={() => recoverRun(lastRunId)}
                    className={cn('px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText, smallTextSize, semiBoldText)}
                  >
                    {t('adminTesting.bork.recoverLastRun')}
                  </button>
                )}
              </div>
            )}

            {summary && (
              <>
                <BorkFetchResults
                  summary={summary}
                  selection={{
                    selected: selectedDays,
                    onToggleDay: toggleDay,
                    onSelectAll: () => setSelectedDays(new Set(applicableDayKeys(summary))),
                    onSelectIssues: () => setSelectedDays(new Set(issueDayKeys(summary))),
                    onSelectNone: () => setSelectedDays(new Set()),
                  }}
                  t={t}
                />

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => applySelected('apply')}
                    disabled={!canApply}
                    className={cn('px-4 py-2 rounded-lg bg-sky-600 dark:bg-sky-600 text-white dark:text-white hover:bg-sky-700 dark:hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
                  >
                    {applying === 'apply' ? t('adminTesting.bork.applying') : t('adminTesting.bork.applySelected')}
                  </button>
                  <button
                    onClick={() => applySelected('override')}
                    disabled={!canApply}
                    className={cn('px-4 py-2 rounded-lg border-2 border-red-600 dark:border-red-500 bg-red-600 dark:bg-red-600 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
                  >
                    {applying === 'override' ? t('adminTesting.bork.applying') : t('adminTesting.bork.overrideSelected')}
                  </button>
                </div>

                {applyError && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm whitespace-pre-wrap">{applyError}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <BorkConnectionSlideOver
        open={formOpen}
        connection={editing}
        restaurants={restaurants}
        takenRestaurantIds={connections.map((c) => c.restaurantId)}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); reloadConnections(); }}
        t={t}
      />
    </div>
  );
}
